import React, { useState, useMemo, memo, useContext, useEffect, useRef } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef, IHeaderParams } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-material.css'
import { agConfigSideBar, tableCellStyle } from '../../../../utils/AgGridConfig'
import { Grant } from '../../../../generated/graphql'
import { ActionRender, CompanyNameRender, PlanNameRender } from './GrantAGComponent'
import { GrantCalculatorDrawer } from './GrantCalculatorDrawer'
import { AddGrantsPopup } from './AddGrantsPopup'
import { getUserId } from '../../../../utils/SessionStorageUtils'
import { isoToDate } from '../../../../utils/FormatDate'
import {
  formatCurrency,
  formatNumber,
  getCurrency,
  getSymbolOfCurrency,
} from '../../../../utils/CurrencyFormatter'
import { GlobalContext } from '../../store/context/globalContext'
import { generateVestingData } from './VestingScheduleGraph'
import { KTSVG } from '../../../../_metronic/helpers'

// Existing interfaces and components
export interface NextVestingInfo {
  nextVestingDate: string | null
  nextVestingIncremental: number
  nextVestingCumulative: number
  daysUntilNext: number | null
}

function calculateNextVestingInfo(grant: Grant): NextVestingInfo | null {
  try {
    if (!grant.vestingSchedules || grant.vestingSchedules.length === 0) {
      return null
    }
    const vestingSchedule = grant.vestingSchedules[0]
    if (!vestingSchedule) {
      return null
    }
    const cliffDurationMonths = (vestingSchedule as any).cliffDurationMonths
    const totalVestingDurationMonths = (vestingSchedule as any).totalVestingDurationMonths
    const vestingFrequency = (vestingSchedule as any).vestingFrequency
    if (
      !grant.grantDate ||
      !grant.numberGranted ||
      cliffDurationMonths == null ||
      totalVestingDurationMonths == null ||
      !vestingFrequency
    ) {
      return null
    }
    const vestingData = generateVestingData(
      grant.grantDate,
      grant.numberGranted,
      grant.vestedOptions || 0,
      cliffDurationMonths,
      totalVestingDurationMonths,
      vestingFrequency
    )
    if (vestingData.length === 0) return null
    const today = new Date()
    const nextVestingPoint = vestingData.find((point) => new Date(point.date) > today)
    if (!nextVestingPoint) return null
    const currentIndex = vestingData.findIndex((point) => point.date === nextVestingPoint.date)
    const previousPoint = currentIndex > 0 ? vestingData[currentIndex - 1] : { vested: 0 }
    const incrementalOptions = nextVestingPoint.vested - previousPoint.vested
    const daysUntilNext = Math.ceil(
      (new Date(nextVestingPoint.date).getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    )
    return {
      nextVestingDate: nextVestingPoint.date,
      nextVestingIncremental: incrementalOptions,
      nextVestingCumulative: nextVestingPoint.vested,
      daysUntilNext,
    }
  } catch (error) {
    console.error('Error calculating next vesting info:', error)
    return null
  }
}

export const NextVestingCell = memo((params: any) => {
  const { userCurrencyFormat } = params.context
  const nextVestingInfo = useMemo(() => calculateNextVestingInfo(params.data), [params.data])
  const nextVestingDate = nextVestingInfo?.nextVestingDate || params.data.nextVestingDate
  const nextVestingOptions =
    nextVestingInfo?.nextVestingIncremental || params.data.nextVestingOptions
  const daysUntilNext = nextVestingInfo?.daysUntilNext || params.data.daysUntilNext
  return (
    <div className='d-flex flex-column'>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {nextVestingDate ? isoToDate(nextVestingDate) : '-'}
      </span>
      {nextVestingOptions > 0 && (
        <small className='text-gray-700' style={{ fontSize: '10px', lineHeight: '1.2' }}>
          {nextVestingOptions.toLocaleString(userCurrencyFormat)} options
        </small>
      )}
    </div>
  )
})

export const VestedCell = memo((params: any) => (
  <span>
    {params.value}{' '}
    <span className='badge bg-light-primary text-primary ms-1'>{params.data.vestedPercent}</span>
    {params.data.cliff && <span className='text-muted small ms-2'>{params.data.cliff}</span>}
  </span>
))

export const StatusCell = memo((params: any) => {
  const value = params.value?.toLowerCase()
  const isInactive = value === 'inactive'
  const statusLabel = isInactive ? 'In Active' : 'Active'
  const statusClass = isInactive
    ? 'badge bg-light-danger text-danger'
    : 'badge bg-light-success text-success'
  return <span className={statusClass}>{statusLabel}</span>
})

export const NumberGrantedCell = memo((params: any) => {
  const numberGranted = params.value || 0
  const vestedOptions = params.data.vestedOptions || 0
  const unvestedOptions = numberGranted - vestedOptions
  const { userCurrencyFormat } = params.context
  return (
    <div className='d-flex flex-column'>
      <span>{numberGranted.toLocaleString(userCurrencyFormat)}</span>
      {unvestedOptions > 0 && (
        <small className='text-gray-700' style={{ fontSize: '10px', lineHeight: '1.2' }}>
          {unvestedOptions.toLocaleString(userCurrencyFormat)} unvested
        </small>
      )}
    </div>
  )
})

export const FMVCell = memo((params: any) => {
  const fmvInUserCurrency = params.data.companyFmvinUserCurrency
  const fmvDate = params.data.fmvDate
  const companyCurrency = params.data.companyCurrency
  const strikePriceInUserCurrency = params.data.strikePriceinUserCurrency
  const { userCurrency, userCurrencyFormat } = params.context
  const fmvValue =
    fmvInUserCurrency && !isNaN(parseFloat(fmvInUserCurrency))
      ? parseFloat(fmvInUserCurrency)
      : undefined
  const strikePriceValue =
    strikePriceInUserCurrency && !isNaN(parseFloat(strikePriceInUserCurrency))
      ? parseFloat(strikePriceInUserCurrency)
      : undefined
  const isUnderwater =
    fmvValue !== undefined && strikePriceValue !== undefined && fmvValue < strikePriceValue
  return (
    <span>
      {fmvInUserCurrency ? (
        <div className='d-flex flex-column'>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>
              {getSymbolOfCurrency(userCurrency)}
              {fmvValue !== undefined
                ? formatNumber({
                    value: fmvValue,
                    format: userCurrencyFormat,
                  })
                : '-'}
            </span>
            {fmvDate && (
              <small className='text-gray-700' style={{ fontSize: '10px', lineHeight: '1.2' }}>
                {`(${isoToDate(fmvDate)})`}
              </small>
            )}
            {isUnderwater && (
              <span
                className='badge bg-danger-active text-white'
                style={{ fontSize: '10px' }}
                title='Loss: Strike > FMV'
              >
                Underwater
              </span>
            )}
          </span>
          {companyCurrency !== userCurrency && (
            <small className='text-gray-700' style={{ fontSize: '10px', lineHeight: '1.2' }}>
              {companyCurrency}{' '}
              {params.data.companyFMV !== undefined
                ? formatNumber({
                    value: Number(params.data.companyFMV),
                    format: getCurrency(companyCurrency),
                  })
                : '-'}
            </small>
          )}
        </div>
      ) : (
        <span
          className='text-small'
          style={{
            color: '#f59f0a',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '0px',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span
              style={{
                width: '6px',
                height: '6px',
                backgroundColor: '#f59f0a',
                borderRadius: '50%',
                display: 'inline-block',
              }}
            />
            Missing FMV
          </span>
          <button
            className='px-1'
            style={{
              fontSize: '11px',
              color: '#f59f0a',
              textDecoration: 'underline',
              background: 'none',
              border: 'none',
              marginTop: '0px',
              padding: 0,
              cursor: 'pointer',
            }}
            onClick={() => params.context.openEditModal?.(params.data)}
          >
            Add FMV
          </button>
        </span>
      )}
    </span>
  )
})

export const StrikePriceCell = memo((params: any) => {
  const strikePriceInUserCurrency = params.data.strikePriceinUserCurrency
  const companyCurrency = params.data.companyCurrency
  const companyStrikePrice = params.data.exercisePriceOrSarValue
  const { userCurrency, userCurrencyFormat } = params.context
  const strikePriceValue =
    strikePriceInUserCurrency && !isNaN(parseFloat(strikePriceInUserCurrency))
      ? parseFloat(strikePriceInUserCurrency)
      : undefined
  return (
    <span>
      {strikePriceInUserCurrency && (
        <div className='d-flex flex-column'>
          <span>
            {getSymbolOfCurrency(userCurrency)}
            {strikePriceValue !== undefined
              ? formatNumber({
                  value: strikePriceValue,
                  format: userCurrencyFormat,
                })
              : '-'}
          </span>
          {companyCurrency !== userCurrency && (
            <small className='text-gray-700' style={{ fontSize: '10px', lineHeight: '1.2' }}>
              {companyCurrency}{' '}
              {companyStrikePrice !== undefined
                ? formatNumber({
                    value: Number(companyStrikePrice),
                    format: getCurrency(companyCurrency),
                  })
                : '-'}
            </small>
          )}
        </div>
      )}
    </span>
  )
})

export type ChartDataPoint = {
  date: string
  vested: number
}

interface CompanyCardProps {
  companyName: string
  grantsCount: number
  vestedOptions: number
  vestedValue: number
  unexercisedValue: string
  grants: Grant[]
  openEditModal?: (data: any) => void
  onOpenCalculator?: (data: any) => void
}

const CompanyCard: React.FC<CompanyCardProps> = ({
  companyName,
  grantsCount,
  vestedOptions,
  vestedValue,
  grants,
  openEditModal,
  onOpenCalculator,
}) => {
  const userId = getUserId()
  const { state } = useContext(GlobalContext)
  const { userCurrency } = state
  const userCurrencyFormat = getCurrency(userCurrency)

  const [open, setOpen] = useState(false)
  const [selectedGrant, setSelectedGrant] = useState<Grant | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editGrant, setEditGrant] = useState<Grant | null>(null)
  const [showCalculator, setShowCalculator] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [calculatorGrant, setCalculatorGrant] = useState<Grant | null>(null)
  const [columnApi, setColumnApi] = useState<any>(null)
  const [isColumnOpen, setIsColumnOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  // const gridRef = useRef<any>(null)
  const gridApi = useRef<any>(null)

  const rows = useMemo(() => grants ?? [], [grants])

  const onGridReady = (params: any) => {
    setColumnApi(params.columnApi)
    gridApi.current = params
  }

  const openToolPanel = (key: any) => {
    if (key === 'columns') {
      console.log('Inside')
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key)
        else gridApi?.current?.api?.closeToolPanel()
        console.log('Inside Grid', isColumnOpen)
        setIsColumnOpen((state) => !state)
        setIsFilterOpen(false)
      }
    }
  }

  const handleToggleColumns = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (isColumnOpen) {
      gridApi.current?.api.closeToolPanel()
    } else {
      openToolPanel('columns')
    }
    setIsColumnOpen(!isColumnOpen)
  }

  const ColumnSelectorHeader = (props: IHeaderParams) => {
    const { columnApi } = props

    if (!columnApi) {
      return <span>Loading...</span>
    }
  }

  const componentsRegistry = useMemo(
    () => ({
      vestedCell: VestedCell,
      statusCell: StatusCell,
      fmvCell: FMVCell,
      actionsCell: ActionRender,
      companyName: memo(CompanyNameRender),
      planName: memo(PlanNameRender),
      NumberGrantedCell: NumberGrantedCell,
      strikePriceCell: StrikePriceCell,
      nextVestingCell: NextVestingCell,
      columnSelectorHeader: ColumnSelectorHeader,
    }),
    []
  )

  const hasMissingFMV = useMemo(() => {
    return grants.some((grant) => !grant.companyFMV)
  }, [grants])

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Plan Name',
        field: 'planName',
        cellRenderer: 'planName',
        cellStyle: tableCellStyle,
        initialWidth: 100,
        minWidth: 200,
        textwrap: true,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipValueGetter: (params) => params.value || '',
        flex: 2,
      },
      {
        headerName: 'Grant Date',
        field: 'grantDate',
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: any) => {
            if (cellValue == null) {
              return 0
            }
            const dateParts = cellValue.split('-')
            const day = Number(dateParts[2]?.split('T')[0])
            const month = Number(dateParts[1]) - 1
            const year = Number(dateParts[0])
            const cellDate = new Date(year, month, day)
            if (cellDate < dateFromFilter) {
              return -1
            } else if (cellDate > dateFromFilter) {
              return 1
            }
            return 0
          },
        },
        cellRenderer: (data: any) => (
          <div className='pt-2'>
            <p>{data.data.grantDate ? isoToDate(data.data.grantDate) : '-'}</p>
          </div>
        ),
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipValueGetter: (params) => params.value || '',
      },
      {
        headerName: 'Number Granted',
        field: 'numberGranted',
        cellStyle: tableCellStyle,
        cellRenderer: 'NumberGrantedCell',
        minWidth: 120,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipValueGetter: (params) => {
          const numberGranted = params.value || 0
          const vestedOptions = params.data.vestedOptions || 0
          const unvestedOptions = numberGranted - vestedOptions
          return `${numberGranted.toLocaleString(
            userCurrencyFormat
          )} granted, ${unvestedOptions.toLocaleString(userCurrencyFormat)} unvested`
        },
        flex: 1,
      },
      {
        headerName: 'Vested',
        field: 'vestedOptions',
        cellRenderer: 'vestedCell',
        cellStyle: tableCellStyle,
        minWidth: 120,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipValueGetter: (params) => `${params.value || ''} `,
        flex: 1,
      },
      {
        headerName: 'Exercised',
        field: 'numberExercised',
        cellStyle: tableCellStyle,
        minWidth: 120,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
        tooltipValueGetter: (params) => params.value?.toLocaleString(userCurrencyFormat) || '',
        flex: 1,
        valueFormatter: (params) => params.value?.toLocaleString(userCurrencyFormat) || '',
      },
      {
        headerName: `FMV (${getSymbolOfCurrency(userCurrency)})`,
        field: 'companyFmvinUserCurrency',
        cellRenderer: 'fmvCell',
        valueFormatter: (params) =>
          params.value
            ? `${getSymbolOfCurrency(userCurrency)}${formatNumber({
                value: Number(params.value),
                format: userCurrencyFormat,
              })}`
            : '',
        cellStyle: tableCellStyle,
        minWidth: 140,
        sortable: true,
        hide: true,
        menuTabs: ['filterMenuTab'],
        tooltipValueGetter: (params) => {
          const fmvValue = params.value
            ? `${getSymbolOfCurrency(userCurrency)}${Number(params.value).toLocaleString(
                userCurrencyFormat
              )}`
            : ''
          const fmvDate = params.data.fmvDate
            ? `as of ${new Date(params.data.fmvDate)
                .toLocaleDateString(userCurrencyFormat, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
                .replace(/\//g, '-')}`
            : ''
          return fmvValue ? `${fmvValue}${fmvDate ? ` (${fmvDate})` : ''}` : ''
        },
        flex: 1,
        headerTooltip: 'Fair Market Value - Current value per share',
      },
      {
        headerName: `Strike Price (${getSymbolOfCurrency(userCurrency)})`,
        field: 'strikePriceinUserCurrency',
        cellRenderer: 'strikePriceCell',
        valueFormatter: (params) =>
          params.value
            ? `${getSymbolOfCurrency(userCurrency)}${formatNumber({
                value: Number(params.value),
                format: userCurrencyFormat,
              })}`
            : '',
        cellStyle: tableCellStyle,
        minWidth: 120,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipValueGetter: (params) => {
          const strikePrice = params.value
            ? `${getSymbolOfCurrency(userCurrency)}${Number(params.value).toLocaleString(
                userCurrencyFormat
              )}`
            : ''
          const grantDate = params.data.grantDate
            ? `as of ${new Date(params.data.grantDate)
                .toLocaleDateString(userCurrencyFormat, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
                .replace(/\//g, '-')}`
            : ''
          return strikePrice ? `${strikePrice}${grantDate ? ` (${grantDate})` : ''}` : ''
        },
        flex: 1,
        headerTooltip: 'Price you pay to exercise each option',
      },
      {
        headerName: 'Next Vesting',
        field: 'nextVestingDate',
        cellRenderer: 'nextVestingCell',
        cellStyle: tableCellStyle,
        minWidth: 120,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipValueGetter: (params) => {
          const nextVestingInfo = calculateNextVestingInfo(params.data)
          const nextVestingDate = nextVestingInfo?.nextVestingDate || params.data.nextVestingDate
          const nextVestingOptions =
            nextVestingInfo?.nextVestingIncremental || params.data.nextVestingOptions
          const formattedDate = nextVestingDate ? isoToDate(nextVestingDate) : '-'
          const formattedOptions = nextVestingOptions
            ? `${nextVestingOptions.toLocaleString(userCurrencyFormat)} options`
            : ''
          return formattedOptions ? `${formattedDate}, ${formattedOptions}` : formattedDate
        },
        flex: 1,
      },
      {
        headerName: 'Expiry',
        field: 'expiryDate',
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: any) => {
            if (cellValue == null) {
              return 0
            }
            const dateParts = cellValue.split(' ')
            const day = Number(dateParts[2]?.split('T')[0])
            const month = Number(dateParts[1]) - 1
            const year = Number(dateParts[0])
            const cellDate = new Date(year, month, day)
            if (cellDate < dateFromFilter) {
              return -1
            } else if (cellDate > dateFromFilter) {
              return 1
            }
            return 0
          },
        },
        cellRenderer: (data: any) => (
          <div className='pt-2'>
            <p>{data.data.expiryDate ? isoToDate(data.data.expiryDate) : '-'}</p>
          </div>
        ),
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: 'statusCell',
        cellStyle: tableCellStyle,
        minWidth: 120,
        sortable: true,
        menuTabs: ['filterMenuTab'],
        tooltipValueGetter: (params) => params.value || '',
        flex: 1,
      },
      {
        headerName: '',
        field: 'actions',
        pinned: 'right',
        overflow: 'visible',
        width: 120,
        sortable: false,
        filter: false,
        cellRenderer: ActionRender,
        cellRendererParams: {
          openEditModal: (grant: Grant) => {
            setSelectedGrant(grant)
            setShowEditModal(true)
          },
          onOpenCalculator: (grant: Grant) => {
            setCalculatorGrant(grant)
            setShowCalculator(true)
          },
          onAddVesting: (grant: Grant) => {
            setEditGrant(grant)
            setShowModal(true)
          },
        },
        colId: 'action-column',
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        menuTabs: [],
        tooltipValueGetter: () => 'Actions',
        cellStyle: tableCellStyle,
        headerComponent: ColumnSelectorHeader,
      },
    ],
    [userCurrency, userCurrencyFormat]
  )

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ['reset'],
        maxNumConditions: 5,
      },
      minWidth: 100,
      filter: true,
      resizable: true,
      flex: 1,
      cellStyle: tableCellStyle,
    }),
    []
  )

  return (
    <div className='card mb-2' style={{ overflow: 'hidden' }}>
      {showCalculator && calculatorGrant && (
        <GrantCalculatorDrawer
          isOpen={showCalculator}
          onClose={() => {
            setShowCalculator(false)
            setCalculatorGrant(null)
          }}
          grant={calculatorGrant}
        />
      )}
      {showEditModal && editGrant && (
        <AddGrantsPopup
          showModal={showEditModal}
          handleClose={() => {
            setShowEditModal(false)
            setEditGrant(null)
          }}
          userId={userId!}
          grantData={editGrant}
          onGrantCreated={() => {
            setShowEditModal(false)
            setEditGrant(null)
          }}
          isEdit={true}
        />
      )}

      <div
        className='card-header d-flex justify-content-between align-items-center'
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen(!open)}
      >
        <div className='d-flex align-items-center gap-1'>
          <button className='btn btn-sm btn-icon'>
            <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
          </button>
          <div className='d-flex flex-column'>
            <span className='fw-bold'>{companyName}</span>
            <div className='d-flex align-items-center gap-3 mt-1'>
              <span className='text-gray-700'>
                {grantsCount} grant{grantsCount > 1 ? 's' : ''} •{' '}
                {vestedOptions.toLocaleString(userCurrencyFormat)} vested options
              </span>
            </div>
          </div>
        </div>

        <div className='d-flex align-items-center gap-4'>
          {open && (
            <div style={{ position: 'relative' }}>
              <button
                type='button'
                onClick={handleToggleColumns}
                className={`px-4 rounded border border-1 h-38px fw-normal ms-4 ${
                  isColumnOpen
                    ? 'bg-transparent  text-primary border-primary'
                    : 'bg-transparent border-secondary-txt text-hover-primary '
                }`}
                aria-label='Toggle column selector'
              >
                <KTSVG
                  path='/media/icons/duotune/layouts/lay004.svg'
                  className={`svg-icon-3 ${isColumnOpen ? 'text-primary' : 'text-secondary-txt'}`}
                />
                Columns
              </button>
            </div>
          )}

          <div className='d-flex flex-column align-items-end'>
            <span
              className='fw-bold'
              data-toggle='tooltip'
              data-placement='top'
              title={Number(vestedValue).toLocaleString(userCurrencyFormat)}
            >
              {getSymbolOfCurrency(userCurrency)}
              {vestedValue &&
                formatNumber({
                  value: Number(vestedValue),
                  format: userCurrencyFormat,
                })}
            </span>
            <span className='text-gray-700 mt-1 small'>Vested Value</span>

            {hasMissingFMV && (
              <span
                style={{
                  color: '#f59f0a',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginTop: '4px',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  const grantWithMissingFMV = grants.find((grant) => !grant.companyFMV) 
                  if (grantWithMissingFMV) {
                    setShowEditModal(true)
                    setEditGrant(grantWithMissingFMV)
                  }
                }}
              >
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    backgroundColor: '#f59f0a',
                    borderRadius: '50%',
                    display: 'inline-block',
                  }}
                />
                FMV Missing
              </span>
            )}
          </div>
        </div>
      </div>

      {open && (
        <div className='card-body'>
          <div
            className='w-100 p-9 pb-4 px-0 pt-0 ag-theme-material'
            style={{ height: 'auto', overflowY: 'scroll' }}
          >
            <AgGridReact
              ref={gridApi}
              sideBar={agConfigSideBar}
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              components={componentsRegistry}
              context={{
                openEditModal,
                onOpenCalculator,
                userCurrency,
                userCurrencyFormat,
              }}
              domLayout='autoHeight'
              suppressRowClickSelection
              headerHeight={36}
              rowHeight={36}
              ensureDomOrder={true}
              suppressRowTransform={true}
              suppressCopyRowsToClipboard={true}
              suppressCopySingleCellRanges={true}
              suppressCellFocus={true}
              suppressMenuHide={false}
              suppressHorizontalScroll={false}
              suppressMovableColumns={true}
              onGridReady={onGridReady}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default CompanyCard
